import { P2GAlert, P2GBox, P2GLink, P2GFade, P2GText, P2GButton } from '@parcel2go/core-ui';
import { ReactNode, useMemo } from 'react';
import { ConsignmentApiModel, Validation } from '~/lib/api/smartsend/types';
import { SmartSendItemGroup } from '../../item/SmartSendGroup';
import { SplitWithDropDown } from '../SplitWithDropDown';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import LabelPrintingIconWithQuantity from './LabelPrintingIconWithQuantity';
import PreQuoteValidation, { PostQuoteValidation } from '~/apps/smartsend/lib/helpers/SmartSendValidation';
import { onlyUnique } from '~/lib/utils/array';
import { consignmentParcelBound } from '~/apps/smartsend/lib/parcels/bounds';
import { ErrorCodeMessage } from '../../item/ErrorCodeMessage';
import ConsignmentSummaryParcels from './ConsignmentSummaryParcels';
import { ErrorsCoveredByContentsSelection } from '../../ErrorsCoveredByContentsSelection';
import { ParcelCountPicker } from '../ParcelCountPicker';

export interface props {
  consignment: ConsignmentApiModel;
  consignmentParcels: number;
  setExpand: (e: boolean) => void;
  generateConsignment: (x: number) => void;
  group: SmartSendItemGroup;
  serviceSupportsConsignments: boolean;
  splitConsignmentControl: ReactNode;
  valid: boolean;
  children?: ReactNode;
  hiddenRowFields?: string[];
  /** Supply to let the customer drag content rows between the parcel cards below. */
  onDragRowToParcel?: (contentUID: string, targetParcelUID: string) => void;
}

const ConsignmentSummaryMessage: React.FC<{ valid: boolean; children?: ReactNode }> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  if (!props.valid) {
    return (
      <P2GBox pt={1}>
        <P2GFade in>
          <P2GText variant="body2" component="p" colour="error">
            {t('ENTER_WEIGHT_ALERT', { defaultValue: 'Please enter a weight to see shipment options.' })}
          </P2GText>
        </P2GFade>
      </P2GBox>
    );
  }
  return (
    <P2GFade in>
      <>{props.children}</>
    </P2GFade>
  );
};

export const ConsignmentSummary: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const consignmentErrors = useMemo(() => {
    const item = props.group.Items[0];

    if (!props.consignment || !item) {
      return [];
    }

    // Errors and Warnings are reset while QuoteState is kept. QuoteState is what PostQuoteValidation's
    // PRODUCT-* and address checks are derived from, but its ERROR list is a quote-time snapshot: an
    // ITEM-WEIGHT raised against weightless parcels was copied in by startValidation and so survived the
    // user fixing the weights, clearing only on the next requote. The block asked for something already
    // done, and neither Done nor closing the arranger could dismiss it.
    //
    // Validated leads the spread so this is a valid Validation for an item that has never been quoted; a
    // real validation overwrites it.
    const quoteContext: Validation = { Validated: false, ...item.State?.validation, Errors: [], Warnings: [] };

    // Both halves, because PostQuoteValidation re-derives only ITEM-WEIGHT - resetting the inherited list
    // without this would silently drop ITEM-PARCEL-EMPTY, ITEM-VALUE and ITEM-DESCRIPTION, which reach
    // this block today via that same inheritance. PreQuoteValidation reads only the item, never
    // QuoteState, so calling it fresh is pure and makes those checks live too.
    return ErrorsCoveredByContentsSelection(
      [...PreQuoteValidation(item).Errors, ...PostQuoteValidation(item, quoteContext).Errors].filter(onlyUnique)
    );
  }, [props.group.Items, props.consignment]);

  // Only offer counts the allocator will actually honour. A single order line pads out to any count, so
  // it keeps the full range; anything else clamps at its piece count, and offering more than that put
  // options on screen that produced an identical plan - the user picked one and nothing happened.
  const optionCount = useMemo(
    () =>
      props.serviceSupportsConsignments
        ? consignmentParcelBound(
            props.group.Items.flatMap((i) => i.MarketplaceOrderLines ?? []),
            props.consignment
          )
        : 0,
    [props.group.Items, props.consignment, props.serviceSupportsConsignments]
  );

  return (
    <>
      <P2GAlert icon={<LabelPrintingIconWithQuantity qty={props.consignmentParcels} t={t} />}>
        {props.consignment ? (
          <>
            <P2GText variant="body2">
              <Trans
                i18nKey="CONSIGNMENT_SUMMARY_INTRO_BOXES_TEXT"
                t={t}
                values={{
                  parcelCount: props.consignmentParcels
                }}
                defaults="This will be sent in <strong>{{parcelCount}} boxes</strong>. You can change the number of boxes below, or edit the contents of each box in the table below."
              />
            </P2GText>
            <ConsignmentSummaryMessage valid={props.valid}>
              <ParcelCountPicker
                availableParcels={optionCount}
                selected={props.consignmentParcels}
                onSelect={(q) => props.generateConsignment(q)}
                justify="flex-start"
                helperText={null}
              />
            </ConsignmentSummaryMessage>
            <P2GText variant="body2">
              <Trans i18nKey="CONSIGNMENT_SUMMARY_INTRO_SUBTEXT" t={t} defaults="Need these collecting separately?" />
              &nbsp;{props.splitConsignmentControl}
            </P2GText>
          </>
        ) : (
          <P2GText variant="body2">
            <Trans
              i18nKey="CONSIGNMENT_SUMMARY_MESSAGE"
              t={t}
              defaults="This will be sent as a <strong>single-parcel</strong> shipment."
            />
            <ConsignmentSummaryMessage valid={props.valid}>
              <SplitWithDropDown
                groupingControl={props.splitConsignmentControl}
                selected={props.consignmentParcels}
                onClick={(q) => props.generateConsignment(q)}
                options={optionCount}
              >
                <P2GLink>{t('SEND_IN_MULTIPLE_BOXES_LINK', { defaultValue: 'Send in multiple boxes' })}</P2GLink>
              </SplitWithDropDown>
            </ConsignmentSummaryMessage>
          </P2GText>
        )}
      </P2GAlert>

      {consignmentErrors.length > 0 && (
        <P2GAlert type="error" sx={{ mt: 2 }}>
          <P2GText variant="body2">
            {t('CONSIGNMENT_SUMMARY_ERRORS', {
              defaultValue: 'There are errors in the consignment. Please check the details.'
            })}
          </P2GText>
          {consignmentErrors.map((error) => (
            <P2GBox key={error} my={1}>
              <strong>
                <ErrorCodeMessage error={error} />
              </strong>
            </P2GBox>
          ))}
          <P2GBox mt={2}>
            <P2GButton onClick={() => props.setExpand(true)}>{t('FIX_ISSUES_BUTTON', 'Fix Issues')}</P2GButton>
          </P2GBox>
        </P2GAlert>
      )}

      {props.consignment?.Parcels?.some((p) => p.Contents?.length > 0) && (
        <P2GBox mt={4}>
          <ConsignmentSummaryParcels
            consignment={props.consignment}
            currencyCode={props.group.Items[0].CurrencyCode}
            setExpand={props.setExpand}
            highlightManualContents={true}
            hiddenRowFields={props.hiddenRowFields}
            onDragRowToParcel={props.onDragRowToParcel}
          >
            <P2GBox mb={2}>
              <P2GText variant="caption">
                {t('CONSIGNMENT_CUSTOM_SUMMARY_CAPTION', { defaultValue: 'Custom shipment defined by you:' })}
              </P2GText>
            </P2GBox>
          </ConsignmentSummaryParcels>
        </P2GBox>
      )}
    </>
  );
};
